import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, BookOpen, Clock, Plus, ChevronDown, ChevronRight } from '../../icons';
import {
  addTeamMember, listTeamMembers, searchInviteCandidates, invalidateTeamMembers,
} from '../../api/project-teams';
import { useAuthStore } from '../../store/authStore';
import {
  listDivisions, listAllTeams, listDirectMembers, getTeam,
  type DivisionDto, type UserSummary as OrgUserSummary,
} from '../../api/organization';
import { Avatar } from '../../components/shared';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './TeamCreateInviteModal.css';
import './TeamInvitePickerModal.css';

interface TeamInvitePickerModalProps {
  /** 초대할 대상 팀(이미 존재하는 팀). */
  teamId: string;
  /** 헤더 부제에 쓸 팀 이름 — 없으면 부제를 생략한다. */
  teamName?: string | null;
  onClose: () => void;
  /** 1명 이상 실제로 추가됐을 때 호출 — caller 가 멤버 목록을 다시 읽는다. */
  onInvited?: (addedCount: number) => void;
}

/** 선택된 사람 — 검색 결과와 조직도 결과의 공통 최소 형태. */
interface Picked {
  id: string;
  name: string;
  avatarUrl?: string | null;
}

/** 한 번에 펼치는 팀원 수 — 나머지는 "+ N명 더 보기". */
const MEMBER_PREVIEW = 6;

/**
 * TeamInvitePickerModal (2026-08-19) — **기존 팀에 멤버 초대**.
 *
 * 화면은 새 팀 만들기(TeamCreateInviteModal)의 "멤버 초대" 블록과 같은 것을 쓴다:
 * 탭 이원화(최근 협업자 / 부서별 찾기) + 선택 칩 + 조직도 트리(부서 → 팀 → 구성원).
 * 지금까지 태스크 보드의 [팀원 초대] 는 `/project/teams` 로 **화면을 떠나** 버렸는데,
 * 보드를 보다가 사람 하나 넣으려던 흐름이 그때마다 끊겼다. 같은 자리에서 고르고 닫는다.
 *
 * 팀 만들기와 다른 점은 두 가지뿐이다 — 이름/설명 입력이 없고(팀은 이미 있다),
 * 이미 팀에 있는 사람은 후보에서 뺀다(중복 추가 409 방지).
 *
 * 소스 회귀 금지(CLAUDE.md): 신규 파일이고, 스타일은 tci-* 를 **그대로 재사용**한다
 * (시안 일치가 목적이므로 복제하지 않는다). 껍데기(backdrop/shell)만 tip-* 로 새로 둔다 —
 * 이 모달은 ProjectPage.css 가 없는 태스크 보드에서도 열리기 때문이다.
 * body 로 portal 하므로 드롭다운 안에서 열려도 잘리지 않는다.
 */
export function TeamInvitePickerModal({ teamId, teamName, onClose, onInvited }: TeamInvitePickerModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  const [tab, setTab] = useState<'recent' | 'dept'>('recent');
  const [picked, setPicked] = useState<Picked[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addedCount, setAddedCount] = useState(0);

  // 나는 이미 팀에 있다 — 후보에서 뺀다.
  const authUser = useAuthStore((st) => st.user);
  const myId = authUser?.id != null ? String(authUser.id) : '';

  /** 이미 이 팀에 있는 사람 — 조직도 탭에서 걸러 낸다(최근 협업자 탭은 서버가 이미 걸러 준다). */
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());

  // 최근 협업자 탭 — 300ms debounce. 서버의 candidates 는 이미 비멤버만 준다.
  const [q, setQ] = useState('');
  const [candidates, setCandidates] = useState<Picked[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 부서별 찾기 탭 — 조직도(부서 → 팀 → 구성원). 팀 멤버는 펼칠 때 1회만 조회한다.
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [orgTeams, setOrgTeams] = useState<Array<{ id: string; divisionId: string | null; name: string }>>([]);
  const [directMembers, setDirectMembers] = useState<OrgUserSummary[]>([]);
  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({});
  const [teamMembers, setTeamMembers] = useState<Record<string, OrgUserSummary[]>>({});
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>({});
  const [orgLoaded, setOrgLoaded] = useState(false);

  useEscapeClose(finish, submitting);

  // 현재 팀원 — 조직도에서 이미 들어와 있는 사람을 지우기 위한 것. 실패해도 초대는 가능하다(409 로 걸러짐).
  useEffect(() => {
    let alive = true;
    void listTeamMembers(teamId)
      .then((list) => {
        if (!alive) return;
        setMemberIds(new Set((Array.isArray(list) ? list : []).map((m) => String(m.userId))));
      })
      .catch(() => { /* 후보가 조금 더 보일 뿐 — 추가 시 409 로 정리된다. */ });
    return () => { alive = false; };
  }, [teamId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        setSearching(true);
        try {
          const found = await searchInviteCandidates(teamId, q.trim(), 20);
          setCandidates(found.map((c) => ({ id: c.userId, name: c.name, avatarUrl: c.avatarUrl })));
        } catch {
          setCandidates([]);
        } finally {
          setSearching(false);
        }
      })();
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, teamId]);

  // 조직도는 탭을 처음 열 때만 불러온다 — 검색으로 끝낼 사람에게 불필요한 요청을 만들지 않는다.
  useEffect(() => {
    if (tab !== 'dept' || orgLoaded) return;
    setOrgLoaded(true);
    void (async () => {
      const [divs, teams, direct] = await Promise.all([
        listDivisions().catch(() => []),
        listAllTeams().catch(() => []),
        listDirectMembers().catch(() => []),
      ]);
      setDivisions(divs);
      setOrgTeams(teams.map((t) => ({ id: t.id, divisionId: t.divisionId, name: t.name })));
      setDirectMembers(direct);
    })();
  }, [tab, orgLoaded]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);

  /** 이미 팀에 있거나 나 자신이면 조직도에서 감춘다. */
  function selectable(list: OrgUserSummary[]): OrgUserSummary[] {
    return list.filter((u) => u.id !== myId && !memberIds.has(String(u.id)));
  }

  /** 아직 펼치지 않은 팀은 undefined — 인원수 자리를 비워 두기 위해 구분해서 돌려준다. */
  function selectableOf(orgTeamId: string): OrgUserSummary[] | undefined {
    const loaded = teamMembers[orgTeamId];
    return loaded ? selectable(loaded) : undefined;
  }

  function toggle(p: Picked) {
    setPicked((cur) => (cur.some((x) => x.id === p.id) ? cur.filter((x) => x.id !== p.id) : [...cur, p]));
  }

  async function openOrgTeam(orgTeamId: string) {
    setExpandedTeams((e) => ({ ...e, [orgTeamId]: !e[orgTeamId] }));
    if (teamMembers[orgTeamId]) return;
    try {
      const detail = await getTeam(orgTeamId);
      setTeamMembers((m) => ({ ...m, [orgTeamId]: detail.members.map((x) => x.user) }));
    } catch {
      setTeamMembers((m) => ({ ...m, [orgTeamId]: [] }));
    }
  }

  /** 닫기 — 한 명이라도 넣었으면 목록을 갱신하도록 알리고 닫는다. */
  function finish() {
    if (addedCount > 0) onInvited?.(addedCount);
    else onClose();
  }

  async function submit() {
    if (picked.length === 0) { finish(); return; }
    setSubmitting(true);
    setError(null);
    const results = await Promise.allSettled(picked.map((p) => addTeamMember(teamId, { userId: p.id })));
    // 409 = 이미 이 팀의 멤버 — 원하던 결과가 이미 이뤄진 것이라 실패로 세지 않는다.
    const failed = picked.filter((_, i) => {
      const r = results[i];
      if (r?.status !== 'rejected') return false;
      return (r.reason as { response?: { status?: number } })?.response?.status !== 409;
    });
    const ok = picked.length - failed.length;
    invalidateTeamMembers(teamId);
    if (failed.length > 0) {
      // 일부만 실패 — 모달을 열어 둔 채 누가 실패했는지 알린다. 성공한 사람은 칩에서 뺀다.
      setAddedCount((n) => n + ok);
      setPicked(failed);
      setError(tProject('project.invite.pick.err_n', { names: failed.map((f) => f.name).join(', ') }));
      setSubmitting(false);
      return;
    }
    onInvited?.(addedCount + ok);
  }

  const body = (
    <div
      className="tip-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) finish(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.invite.title')}
    >
      <div ref={trapRef} className="tip-modal tci-modal">
        <header className="tci-head">
          <div>
            <h2 className="tci-title">{tProject('project.invite.title')}</h2>
            <p className="tci-sub">
              {teamName
                ? tProject('project.invite.pick.subtitle_team', { team: teamName })
                : tProject('project.invite.pick.subtitle')}
            </p>
          </div>
          <button type="button" className="tip-close" onClick={finish} disabled={submitting} aria-label={tProject('project.btn.close')} data-tip={tProject('project.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <div className="tci-body">
          <div className="tci-label">{tProject('project.invite.pick.label')}</div>

          <div className="tci-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'recent'}
              className={`tci-tab${tab === 'recent' ? ' tci-tab--on' : ''}`}
              onClick={() => setTab('recent')}
            >
              <Clock size={13} aria-hidden />
              {tProject('project.team.aw.tab_recent')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'dept'}
              className={`tci-tab${tab === 'dept' ? ' tci-tab--on' : ''}`}
              onClick={() => setTab('dept')}
            >
              <BookOpen size={13} aria-hidden />
              {tProject('project.team.aw.tab_dept')}
            </button>
          </div>

          {picked.length > 0 ? (
            <div className="tci-picked">
              <span className="tci-picked-count">{tProject('project.team.aw.selected', { n: String(picked.length) })}</span>
              {picked.map((p) => (
                <span key={p.id} className="tci-picked-chip">
                  <Avatar userId={Number(p.id) || 0} name={p.name} src={p.avatarUrl ?? null} size="xxs" />
                  {p.name}
                  <button
                    type="button"
                    aria-label={tProject('project.team.aw.remove_aria', { name: p.name })}
                    onClick={() => toggle(p)}
                  >
                    <X size={11} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            tab === 'recent' && <p className="tci-hint">{tProject('project.team.aw.none_selected')}</p>
          )}

          {tab === 'recent' && (
            <>
              <div className="tci-search">
                <Search size={13} aria-hidden />
                <input
                  className="tci-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={tProject('project.team.aw.search_ph')}
                  aria-label={tProject('project.team.aw.search_ph')}
                  disabled={submitting}
                  autoFocus
                />
              </div>
              <ul className="tci-list scrollbar-thin">
                {!searching && candidates.length === 0 && (
                  <li className="tci-empty">{tProject('project.invite.empty')}</li>
                )}
                {candidates.map((u) => (
                  <li key={u.id} className="tci-person">
                    <Avatar userId={Number(u.id) || 0} name={u.name} src={u.avatarUrl ?? null} size="sm" />
                    <span className="tci-person-body">
                      <b>{u.name}</b>
                    </span>
                    <button
                      type="button"
                      className={`tci-pick${pickedIds.has(u.id) ? ' tci-pick--on' : ''}`}
                      aria-label={tProject(pickedIds.has(u.id) ? 'project.team.aw.remove_aria' : 'project.team.aw.add_aria', { name: u.name })}
                      onClick={() => toggle(u)}
                    >
                      {pickedIds.has(u.id) ? <X size={13} aria-hidden /> : <Plus size={14} aria-hidden />}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {tab === 'dept' && (
            <div className="tci-org scrollbar-thin">
              {divisions.length === 0 && orgTeams.length === 0 && directMembers.length === 0 && (
                <p className="tci-empty">{tProject('project.team.aw.empty_org')}</p>
              )}

              {divisions.map((d) => {
                const teams = orgTeams.filter((t) => t.divisionId === d.id);
                const open = openNodes[d.id] ?? false;
                const headcount = teams.reduce((n, t) => n + (selectableOf(t.id)?.length ?? 0), 0);
                return (
                  <div className="tci-node" key={d.id}>
                    <button type="button" className="tci-node-head" onClick={() => setOpenNodes((o) => ({ ...o, [d.id]: !open }))}>
                      {open ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
                      <span className="tci-node-name">{d.name}</span>
                      {headcount > 0 && (
                        <span className="tci-node-count">{tProject('project.team.aw.count_people', { n: String(headcount) })}</span>
                      )}
                    </button>
                    {open && teams.map((t) => (
                      <OrgTeamNode
                        key={t.id}
                        name={t.name}
                        expanded={expandedTeams[t.id] ?? false}
                        members={selectableOf(t.id)}
                        pickedIds={pickedIds}
                        onToggleOpen={() => void openOrgTeam(t.id)}
                        onPick={toggle}
                      />
                    ))}
                    {open && teams.length === 0 && <p className="tci-empty tci-empty--sub">{tProject('project.team.aw.empty_org')}</p>}
                  </div>
                );
              })}

              {/* 부서 없는 팀 — 조직도에서 사라지지 않게 같은 층에 편다. */}
              {orgTeams.filter((t) => !t.divisionId).map((t) => (
                <OrgTeamNode
                  key={t.id}
                  name={t.name}
                  expanded={expandedTeams[t.id] ?? false}
                  members={selectableOf(t.id)}
                  pickedIds={pickedIds}
                  onToggleOpen={() => void openOrgTeam(t.id)}
                  onPick={toggle}
                />
              ))}

              {/* 팀에 속하지 않은 회사 직속 구성원(대표 등). */}
              {selectable(directMembers).map((u) => (
                <div className="tci-person tci-person--flat" key={u.id}>
                  <Avatar userId={Number(u.id) || 0} name={u.name} src={u.avatarUrl ?? null} size="sm" />
                  <span className="tci-person-body">
                    <b>{u.name}</b>
                    <small>{u.position ?? u.email}</small>
                  </span>
                  <button
                    type="button"
                    className={`tci-pick${pickedIds.has(u.id) ? ' tci-pick--on' : ''}`}
                    aria-label={tProject(pickedIds.has(u.id) ? 'project.team.aw.remove_aria' : 'project.team.aw.add_aria', { name: u.name })}
                    onClick={() => toggle({ id: u.id, name: u.name, avatarUrl: u.avatarUrl ?? null })}
                  >
                    {pickedIds.has(u.id) ? <X size={13} aria-hidden /> : <Plus size={14} aria-hidden />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 에러는 푸터 바로 위 — 본문이 스크롤되는 모달이라 본문 안에 두면 눈에 안 들어온다. */}
        {error && <div className="tci-error" role="alert">{error}</div>}

        <footer className="tci-foot">
          <button type="button" className="tci-cancel" onClick={finish} disabled={submitting}>
            {tProject('project.btn.cancel')}
          </button>
          <span className="tip-spacer" />
          <button type="button" className="tci-submit" onClick={() => void submit()} disabled={submitting || picked.length === 0}>
            {picked.length > 0
              ? tProject('project.invite.pick.submit_n', { n: String(picked.length) })
              : tProject('project.invite.pick.submit')}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

interface OrgTeamNodeProps {
  name: string;
  expanded: boolean;
  members: OrgUserSummary[] | undefined;
  pickedIds: Set<string>;
  onToggleOpen: () => void;
  onPick: (p: Picked) => void;
}

/** 조직도 팀 노드 — 펼치면 구성원 체크리스트. 인원이 많으면 앞 6명만 먼저 보여 준다. */
function OrgTeamNode({ name, expanded, members, pickedIds, onToggleOpen, onPick }: OrgTeamNodeProps) {
  const [showAll, setShowAll] = useState(false);
  const list = members ?? [];
  const shown = showAll ? list : list.slice(0, MEMBER_PREVIEW);
  const rest = list.length - shown.length;
  return (
    <div className="tci-node tci-node--team">
      <button type="button" className="tci-node-head" onClick={onToggleOpen}>
        {expanded ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
        <span className="tci-node-name">{name}</span>
        {members && (
          <span className="tci-node-count">{tProject('project.team.aw.count_people', { n: String(list.length) })}</span>
        )}
      </button>
      {expanded && members && list.length === 0 && (
        <p className="tci-empty tci-empty--sub">{tProject('project.invite.pick.all_in')}</p>
      )}
      {expanded && shown.map((u) => (
        <div className="tci-person tci-person--indent" key={u.id}>
          <Avatar userId={Number(u.id) || 0} name={u.name} src={u.avatarUrl ?? null} size="sm" />
          <span className="tci-person-body">
            <b>{u.name}</b>
            <small>{u.position ?? u.email}</small>
          </span>
          <button
            type="button"
            className={`tci-pick${pickedIds.has(u.id) ? ' tci-pick--on' : ''}`}
            aria-label={tProject(pickedIds.has(u.id) ? 'project.team.aw.remove_aria' : 'project.team.aw.add_aria', { name: u.name })}
            onClick={() => onPick({ id: u.id, name: u.name, avatarUrl: u.avatarUrl ?? null })}
          >
            {pickedIds.has(u.id) ? <X size={13} aria-hidden /> : <Plus size={14} aria-hidden />}
          </button>
        </div>
      ))}
      {expanded && rest > 0 && (
        <button type="button" className="tci-more" onClick={() => setShowAll(true)}>
          {tProject('project.team.aw.more_people', { n: String(rest) })}
        </button>
      )}
    </div>
  );
}
